import { IonButton, IonButtons, IonCol, IonIcon, IonItemDivider, IonLabel, IonList, IonRow } from '@ionic/react'
import { useDispatch, useSelector } from 'react-redux'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { trashOutline } from 'ionicons/icons'
import {
  setSceneDescription,
  setNumPtsScene,
  setFirstOnScene,
  setPrOtherAgencies,
  setTransferOfCare,
  setTransferOfCarePNNV,
  setLawnum,
  setFirenum,
  setPrDisaster,
  setPrDispoInstructions,
  setBelongings,
  setPrPtMovedFrom,
  setPrPtPosition,
  setPrPtMovedVia,
  setPrTransportAssess,
  setStretcherPurposeDescr,
  setLevelCarePerProtocol,
  setLevelCarePerProtocolPNNV,
  setFirstOnScenePNNV,
  deleteCadComments,
} from '../store/sceneDescription.actions'
import MultiSelect from '../../../components/multiSelect/MultiSelect.component'
import { getSceneDescriptionSelector } from '../selectors'
import { getSceneDescriptionOptionsSelector } from '../../options/selectors/sceneDescriptionOptions.selector'
import TextArea from '../../../components/textArea/TextArea.component'
import { SceneDescriptionConstants } from '../store/sceneDescription.constants'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import NumberPad from '../../../components/numberPad/NumberPad.component'
import Validation from '../../../components/validation/Validation.component'
import { IPHIComponent } from '../../../components/phi/phi.component'
import SInput from '../../../components/stringInput/StringInput.component'
import { IOption, IOptionWithKey, IPNNVOption } from '../../../common/common.types'
import { getTripInformationSelector } from '../../tripInformation/selectors'
import { FIRE_AGENCY_OPTION, LAW_AGENCY_OPTION } from '../../options/data/optionsData.constants'
import { getServiceConfigSelector } from '../../configurations/selectors/serviceConfig.selector'
import { getDatapointsConfigSelector } from '../../configurations/selectors'
import OtherAgencies from '../otherAgencies/OtherAgencies.component'
import { otherAgencyIsNonEmpty } from '../utils/sceneDescription.util'
import { showAlert } from '../../../components/alert/store/alert.actions'
import './SceneDescription.content.css'

const SceneDescriptionContent: React.FC<IPHIComponent> = (props: IPHIComponent) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { isPHI } = props
  const serviceConfig = useSelector(getServiceConfigSelector)
  const { PR_PTS_ONSCENE, PR_PT_MOVEMENT } = serviceConfig
  const dataPointsConfig = useSelector(getDatapointsConfigSelector)
  const { DP_BELONGINGS, DP_DISASTER, DP_LEVEL_CARE_PER_PROTOCOL, DP_OTHER_AGENCIES } = dataPointsConfig
  const sceneDescriptionState = useSelector(getSceneDescriptionSelector)
  const sceneDescriptionOptionsState = useSelector(getSceneDescriptionOptionsSelector)
  const { disposition } = useSelector(getTripInformationSelector)
  const {
    sceneDescription,
    numPtsScene,
    firstOnScene,
    firstOnScenePNNV,
    levelCarePerProtocol,
    levelCarePerProtocolPNNV,
    prOtherAgencies,
    transferOfCare,
    transferOfCarePNNV,
    lawnum,
    firenum,
    prDisaster,
    prDispoInstructions,
    belongings,
    prPtMovedVia,
    prPtPosition,
    prPtMovedFrom,
    prTransportAssess,
    stretcherPurposeDescr,
    cadComments,
    otherAgencies,
  } = sceneDescriptionState

  const {
    firstOnSceneOptions,
    levelCarePerProtocolOptions,
    prOtherAgenciesOptions,
    transferOfCareOptions,
    prDisasterOptions,
    prPtMovedFromOptions,
    prPtPositionOptions,
    prPtMovedViaOptions,
    prTransportAssessOptions,
    prDispoInstructionsOptions,
  } = sceneDescriptionOptionsState

  useEffect(() => {
    if (prDispoInstructions && prDispoInstructions.length > 0 && disposition?.dispinstructOn !== 1)
      dispatch(setPrDispoInstructions([]))
  }, [disposition])

  useEffect(() => {
    if (!showFirenum() && firenum) {
      dispatch(setFirenum(''))
    }
    if (!showLawnum() && lawnum) {
      dispatch(setLawnum(''))
    }
  }, [prOtherAgencies])

  const sceneDescriptionOnChange = (e: CustomEvent) => {
    dispatch(setSceneDescription(e.detail.value))
  }

  const stretcherPurposeDescrOnChange = (e: CustomEvent) => {
    dispatch(setStretcherPurposeDescr(e.detail.value))
  }

  const belongingsOnChange = (e: CustomEvent) => {
    dispatch(setBelongings(e.detail.value))
  }

  const numPtsSceneOnChange = (numbers: string[]) => {
    dispatch(setNumPtsScene(numbers.join('')))
  }

  const setFirenumOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setFirenum(e.detail.value.trim()))
  }

  const setLawnumOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setLawnum(e.detail.value.trim()))
  }

  const firstOnSceneOnChange = (option: IOption) => {
    dispatch(setFirstOnScene(option))
  }

  const transferOfCareOnChange = (option: IOptionWithKey | null) => {
    dispatch(setTransferOfCare(option))
  }

  const transferOfCarePNNVOnChange = (option: IPNNVOption | null) => {
    if (option) option.key = transferOfCareOptions?.length > 0 ? transferOfCareOptions[0].key : ''
    dispatch(setTransferOfCarePNNV(option))
  }

  const firstOnScenePNNVOnChange = (option: IPNNVOption) => {
    dispatch(setFirstOnScenePNNV(option))
  }

  const levelCarePerProtocolOnChange = (option: IOption) => {
    dispatch(setLevelCarePerProtocol(option))
  }

  const levelCarePerProtocolPNNVOnChange = (option: IPNNVOption) => {
    dispatch(setLevelCarePerProtocolPNNV(option))
  }

  const handleDeleteCadComments = () => {
    const alertState = {
      header: t('sceneDescription.deleteCadCommentsTitle'),
      message: t('sceneDescription.deleteCadCommentsMessage'),
      buttons: [
        { text: t('common.buttons.Cancel'), role: 'cancel' },
        { text: t('common.buttons.Yes'), role: 'destructive', handler: () => dispatch(deleteCadComments()) },
      ],
    }
    dispatch(showAlert(alertState))
  }

  const showOtherAgenciesGeneric = DP_OTHER_AGENCIES === 1 || (prOtherAgencies && prOtherAgencies.length > 0)
  const showOtherAgencies =
    DP_OTHER_AGENCIES === 1 ||
    (otherAgencies &&
      otherAgencies.length > 0 &&
      otherAgencies.some((otherAgency) => otherAgencyIsNonEmpty(otherAgency)))
  const showCadCommentsField = Boolean(cadComments)

  const showFirenum = () => {
    return (
      prOtherAgencies &&
      prOtherAgencies.length > 0 &&
      prOtherAgencies.find((option) => option.id === FIRE_AGENCY_OPTION || option.name === 'Fire')
    )
  }

  const showLawnum = () => {
    return (
      prOtherAgencies &&
      prOtherAgencies.length > 0 &&
      prOtherAgencies.find((option) => option.id === LAW_AGENCY_OPTION || option.name === 'Law')
    )
  }

  return (
    <>
      <IonItemDivider color='light' className='ion-padding'>
        <IonLabel className='grid-item-divider-label' color='medium'>
          {t('fullChart.dispatch.DetailsOfSceneAndOtherRespondingAgencies')}
        </IonLabel>
      </IonItemDivider>
      <IonList>
        <IonRow>
          <IonCol className='picklist'>
            <TextArea
              isPHI={isPHI}
              dataTestID='SceneDescription'
              label={t('sceneDescription.sceneDescription')}
              rows={3}
              cols={20}
              maxlength={4000}
              value={sceneDescription}
              validationId={''}
              onChangeAction={sceneDescriptionOnChange}
            />
            <Validation id={SceneDescriptionConstants.SET_SCENE_DESCRIPTION} />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />

        {PR_PTS_ONSCENE === 1 && (
          <>
            <IonRow>
              <IonCol className='picklist'>
                <NumberPad
                  elementId='NumPtsScene'
                  dataTestId='NumPtsScene'
                  values={numPtsScene ? numPtsScene.toString().split('') : []}
                  label={t('sceneDescription.numPtsScene')}
                  onNumPadClick={numPtsSceneOnChange}
                />
                <Validation id={SceneDescriptionConstants.SET_NUM_PTS_SCENE} />
              </IonCol>
            </IonRow>
            <hr className='field-divider' />
          </>
        )}

        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='FirstOnScene'
              options={firstOnSceneOptions}
              selectedOption={firstOnScene}
              listHeader={t('sceneDescription.firstOnScene')}
              label={t('sceneDescription.firstOnScene')}
              action={firstOnSceneOnChange}
              selectedPNNVOption={firstOnScenePNNV}
              pnnvAction={firstOnScenePNNVOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        {showOtherAgenciesGeneric && (
          <>
            <IonRow>
              <IonCol className='picklist'>
                <MultiSelect
                  dataTestID='PrOtherAgencies'
                  label={t('sceneDescription.prOtherAgencies')}
                  listHeader={t('sceneDescription.prOtherAgencies')}
                  options={prOtherAgenciesOptions}
                  savedValues={prOtherAgencies}
                  action={setPrOtherAgencies}
                />
              </IonCol>
            </IonRow>

            <hr className='field-divider' />
          </>
        )}
        {showOtherAgencies && (
          <>
            <OtherAgencies />
            <hr className='field-divider' />
          </>
        )}

        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='TransferOfCare'
              options={transferOfCareOptions}
              selectedOption={transferOfCare}
              listHeader={t('sceneDescription.TransferOfCare')}
              label={t('sceneDescription.TransferOfCare')}
              action={transferOfCareOnChange}
              selectedPNNVOption={transferOfCarePNNV}
              pnnvAction={transferOfCarePNNVOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <IonRow>
          {showFirenum() && (
            <IonCol className='picklist'>
              <SInput
                label={t('sceneDescription.firenum')}
                dataTestID='Firenum'
                inputValue={firenum}
                onChangeAction={setFirenumOnChange}
              />
              <Validation id={SceneDescriptionConstants.SET_FIRENUM} />
            </IonCol>
          )}
          {showLawnum() && (
            <IonCol className='picklist'>
              <SInput
                label={t('sceneDescription.lawnum')}
                dataTestID='Lawnum'
                inputValue={lawnum}
                onChangeAction={setLawnumOnChange}
              />
              <Validation id={SceneDescriptionConstants.SET_LAWNUM} />
            </IonCol>
          )}
        </IonRow>

        {(showFirenum() || showLawnum()) && <hr className='field-divider' />}

        {DP_DISASTER === 1 && (
          <>
            <IonRow>
              <IonCol className='picklist'>
                <MultiSelect
                  dataTestID='prDisaster'
                  label={t('sceneDescription.prDisaster')}
                  listHeader={t('sceneDescription.prDisaster')}
                  options={prDisasterOptions}
                  savedValues={prDisaster}
                  action={setPrDisaster}
                />
              </IonCol>
            </IonRow>

            <hr className='field-divider' />
          </>
        )}

        {disposition && disposition.dispinstructOn === 1 && (
          <>
            <IonRow>
              <IonCol className='picklist'>
                <MultiSelect
                  dataTestID='PrDispoInstructions'
                  label={t('sceneDescription.prDispoInstructions')}
                  listHeader={t('sceneDescription.prDispoInstructions')}
                  options={prDispoInstructionsOptions}
                  savedValues={prDispoInstructions}
                  action={setPrDispoInstructions}
                  customInput={{
                    textboxEnabledId: null,
                    maxlength: 250,
                    required: true,
                  }}
                />
              </IonCol>
            </IonRow>
            <hr className='field-divider' />
          </>
        )}
        {DP_LEVEL_CARE_PER_PROTOCOL > 0 && (
          <>
            <IonRow>
              <IonCol className='picklist'>
                <SingleSelect
                  dataTestID='LevelCarePerProtocol'
                  options={levelCarePerProtocolOptions}
                  selectedOption={levelCarePerProtocol}
                  listHeader={t('sceneDescription.levelCarePerProtocol')}
                  label={t('sceneDescription.levelCarePerProtocol')}
                  action={levelCarePerProtocolOnChange}
                  selectedPNNVOption={levelCarePerProtocolPNNV}
                  pnnvAction={levelCarePerProtocolPNNVOnChange}
                />
              </IonCol>
            </IonRow>

            <hr className='field-divider' />
          </>
        )}

        {showCadCommentsField && (
          <>
            <IonRow className='ion-align-items-center cad-comments-row'>
              <IonCol className='picklist'>
                <TextArea
                  dataTestID='CadComments'
                  label={t('sceneDescription.cadComments')}
                  rows={3}
                  cols={20}
                  maxlength={1000}
                  value={cadComments}
                  disabled={true}
                  onChangeAction={() => null}
                  className='cad-comments-textarea'
                />
              </IonCol>
              <IonButtons className='cad-comments-delete-button'>
                <IonButton data-testid='DeleteCadComments' onClick={handleDeleteCadComments}>
                  <IonIcon icon={trashOutline} />
                </IonButton>
              </IonButtons>
            </IonRow>
            <hr className='field-divider' />
          </>
        )}

        {DP_BELONGINGS === 1 && (
          <>
            <IonRow>
              <IonCol className='picklist'>
                <TextArea
                  isPHI={isPHI}
                  dataTestID='belongings'
                  label={t('sceneDescription.belongings')}
                  rows={3}
                  cols={20}
                  maxlength={2000}
                  value={belongings}
                  validationId={''}
                  onChangeAction={belongingsOnChange}
                />
                <Validation id={SceneDescriptionConstants.SET_BELONGINGS} />
              </IonCol>
            </IonRow>
            <hr className='field-divider' />
          </>
        )}
      </IonList>

      {PR_PT_MOVEMENT === 1 && (
        <>
          <IonItemDivider color='light' className='ion-padding'>
            <IonLabel className='grid-item-divider-label' color='medium'>
              {t('sceneDescription.patientMovement')}
            </IonLabel>
          </IonItemDivider>
          <IonList>
            <IonRow>
              <IonCol className='picklist'>
                <MultiSelect
                  dataTestID='PrPtMovedVia'
                  label={t('sceneDescription.prPtMovedVia')}
                  listHeader={t('sceneDescription.prPtMovedVia')}
                  options={prPtMovedViaOptions}
                  savedValues={prPtMovedVia}
                  action={setPrPtMovedVia}
                />
              </IonCol>
            </IonRow>

            <hr className='field-divider' />

            <IonRow>
              <IonCol className='picklist'>
                <MultiSelect
                  dataTestID='PrPtPosition'
                  label={t('sceneDescription.prPtPosition')}
                  listHeader={t('sceneDescription.prPtPosition')}
                  options={prPtPositionOptions}
                  savedValues={prPtPosition}
                  action={setPrPtPosition}
                />
              </IonCol>
            </IonRow>

            <hr className='field-divider' />

            <IonRow>
              <IonCol className='picklist'>
                <MultiSelect
                  dataTestID='PrPtMovedFrom'
                  label={t('sceneDescription.prPtMovedFrom')}
                  listHeader={t('sceneDescription.prPtMovedFrom')}
                  options={prPtMovedFromOptions}
                  savedValues={prPtMovedFrom}
                  action={setPrPtMovedFrom}
                />
              </IonCol>
            </IonRow>

            <hr className='field-divider' />

            <IonRow>
              <IonCol className='picklist'>
                <MultiSelect
                  dataTestID='PrTransportAssess'
                  label={t('sceneDescription.prTransportAssess')}
                  listHeader={t('sceneDescription.prTransportAssess')}
                  options={prTransportAssessOptions}
                  savedValues={prTransportAssess}
                  action={setPrTransportAssess}
                />
              </IonCol>
            </IonRow>

            <hr className='field-divider' />

            <IonRow>
              <IonCol className='picklist'>
                <TextArea
                  dataTestID='StretcherPurposeDescr'
                  label={t('sceneDescription.stretcherPurposeDescr')}
                  rows={3}
                  cols={20}
                  maxlength={80}
                  value={stretcherPurposeDescr}
                  validationId={''}
                  onChangeAction={stretcherPurposeDescrOnChange}
                />
                <Validation id={SceneDescriptionConstants.SET_STRETCHER_PURPOSE_DESCR} />
              </IonCol>
            </IonRow>

            <hr className='field-divider' />
          </IonList>
        </>
      )}
    </>
  )
}

export default SceneDescriptionContent
